import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Palette, Upload, Save, Eye, Image as ImageIcon, Type } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function TenantBrandingPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '', primary_color: '#D4AF37', secondary_color: '#F5D76E',
    welcome_message: '', hero_title: '', tagline: '', support_email: '',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await api.get('/api/admin/my-tenant');
      const t = r.data.tenant;
      setTenant(t);
      setForm({
        name: t.name || '',
        primary_color: t.branding?.primary_color || '#D4AF37',
        secondary_color: t.branding?.secondary_color || '#F5D76E',
        welcome_message: t.branding?.welcome_message || '',
        hero_title: t.branding?.hero_title || '',
        tagline: t.branding?.tagline || '',
        support_email: t.settings?.support_email || '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tenant');
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/api/admin/my-tenant/branding', form);
      toast.success('Branding updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post('/api/admin/my-tenant/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploadingLogo(false); }
  };

  const resolveLogoUrl = (url) => url?.startsWith('http') ? url : `${API_BASE}${url}`;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tenant-branding-page">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Palette className="w-6 h-6 text-[#D4AF37]" /> Branding & Customization
        </h2>
        <p className="text-sm text-gray-400 mt-1">Customize your group's landing, login, and home pages</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left 2/3 — Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Tenant Name + Logo */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#D4AF37]" /> Identity</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-300">Group Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-name" />
              </div>
              <div>
                <Label className="text-sm text-gray-300">Logo</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: `linear-gradient(135deg, ${form.primary_color}22, ${form.secondary_color}22)`, border: '1px solid rgba(255,255,255,0.08)' }}>
                    {tenant?.branding?.logo_url ? (
                      <img src={resolveLogoUrl(tenant.branding.logo_url)} alt="Logo" className="w-full h-full object-cover" data-testid="current-logo" />
                    ) : (
                      <span className="text-2xl font-bold" style={{ color: form.primary_color }}>{(form.name || 'T').charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <input type="file" ref={logoInputRef} hidden accept="image/*" onChange={e => uploadLogo(e.target.files[0])} />
                    <Button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                      className="rounded-xl bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30"
                      data-testid="btn-upload-logo">
                      {uploadingLogo ? <div className="spinner w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Logo
                    </Button>
                    <p className="text-[10px] text-gray-500 mt-1">PNG/JPG/WebP, square recommended</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2"><Palette className="w-4 h-4 text-[#D4AF37]" /> Brand Colors</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-300">Primary</Label>
                <div className="flex gap-2 mt-1.5">
                  <input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="w-12 h-11 rounded-xl border border-white/10 bg-transparent cursor-pointer" data-testid="input-primary-color" />
                  <Input value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="flex-1 bg-black/30 border-white/10 text-white h-11 rounded-xl font-mono text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-300">Secondary</Label>
                <div className="flex gap-2 mt-1.5">
                  <input type="color" value={form.secondary_color} onChange={e => setForm(f => ({ ...f, secondary_color: e.target.value }))}
                    className="w-12 h-11 rounded-xl border border-white/10 bg-transparent cursor-pointer" data-testid="input-secondary-color" />
                  <Input value={form.secondary_color} onChange={e => setForm(f => ({ ...f, secondary_color: e.target.value }))}
                    className="flex-1 bg-black/30 border-white/10 text-white h-11 rounded-xl font-mono text-sm" />
                </div>
              </div>
            </div>
          </div>

          {/* Texts */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2"><Type className="w-4 h-4 text-[#D4AF37]" /> Text Content</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-300">Hero Title</Label>
                <Input value={form.hero_title} onChange={e => setForm(f => ({ ...f, hero_title: e.target.value }))}
                  placeholder={`Welcome to ${form.name || 'Your Group'}`}
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-hero-title" />
              </div>
              <div>
                <Label className="text-sm text-gray-300">Tagline / Subtitle</Label>
                <Input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))}
                  placeholder="Fast deposits, instant withdrawals, 24/7 support"
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-tagline" />
              </div>
              <div>
                <Label className="text-sm text-gray-300">Home Welcome Message</Label>
                <Textarea value={form.welcome_message} onChange={e => setForm(f => ({ ...f, welcome_message: e.target.value }))}
                  placeholder="Shown at the top of player's home tab"
                  className="mt-1.5 bg-black/30 border-white/10 text-white rounded-xl min-h-[80px]" rows={3} data-testid="input-welcome-message" />
              </div>
              <div>
                <Label className="text-sm text-gray-300">Support Email</Label>
                <Input value={form.support_email} onChange={e => setForm(f => ({ ...f, support_email: e.target.value }))}
                  placeholder="support@yourgroup.com"
                  className="mt-1.5 bg-black/30 border-white/10 text-white h-11 rounded-xl" data-testid="input-support-email" />
              </div>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="w-full h-12 rounded-2xl font-bold text-base"
            style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`, color: '#000' }}
            data-testid="btn-save-branding">
            {saving ? <div className="spinner w-5 h-5 mr-2" /> : <Save className="w-5 h-5 mr-2" />}
            Save All Changes
          </Button>
        </div>

        {/* Right 1/3 — Live Preview */}
        <div className="space-y-5">
          <div className="rounded-2xl p-5 sticky top-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-4"><Eye className="w-4 h-4" /> Live Preview</h3>
            {/* Preview header */}
            <div className="rounded-xl p-3 flex items-center gap-2 mb-3" style={{ background: 'rgba(0,0,0,0.5)', border: `1px solid ${form.primary_color}33` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden" style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})` }}>
                {tenant?.branding?.logo_url
                  ? <img src={resolveLogoUrl(tenant.branding.logo_url)} className="w-full h-full object-cover" />
                  : <span className="text-black font-bold">{(form.name || 'T').charAt(0).toUpperCase()}</span>}
              </div>
              <span className="font-bold text-sm" style={{ background: `linear-gradient(90deg, ${form.primary_color}, ${form.secondary_color})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{form.name || 'Your Group'}</span>
            </div>
            {/* Preview hero */}
            <div className="rounded-xl p-4 text-center mb-3" style={{ background: `linear-gradient(135deg, ${form.primary_color}15, ${form.secondary_color}05)`, border: `1px solid ${form.primary_color}33` }}>
              <h3 className="text-base font-bold mb-1" style={{ color: form.primary_color }}>{form.hero_title || `Welcome to ${form.name || 'Your Group'}`}</h3>
              <p className="text-[11px] text-gray-400">{form.tagline || 'Your tagline appears here'}</p>
            </div>
            {/* Preview CTA button */}
            <button className="w-full py-2.5 rounded-full text-sm font-bold" style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`, color: '#000' }}>
              Sign Up Button Preview
            </button>
            <div className="mt-4 pt-4 border-t border-white/5 text-[11px] text-gray-500 space-y-1">
              <p>URL: <span className="text-gray-300 font-mono">/{tenant?.slug}</span></p>
              <p>Login: <span className="text-gray-300 font-mono">/{tenant?.slug}/auth</span></p>
              <a href={`/${tenant?.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline mt-1" data-testid="link-visit-tenant">Open landing page →</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
